import { useEffect, useMemo, useState } from 'react';
import { act, dial, formatNumber, hangup, setMyState, toggleHold, toggleMute, toggleRecordingPause } from '../lib/actions';
import { api } from '../lib/api';
import { softphone } from '../lib/softphone';
import { myCall, myTestCall, otherParty, STATE_COLOR, STATE_LABEL, useNova } from '../lib/store';
import type { AgentState, InteractionRow, LiveCall } from '../lib/types';
import { ContextPanel, Subject } from './ContextPanel';
import { SignalRing } from './SignalRing';
import { Button, duration, Elapsed, Icon, Kbd, StateDot, useNow } from './ui';

/**
 * The agent's workspace, in three columns: work items on the left, the open
 * interaction in the middle, the customer on the right. A ringing or live
 * call always comes to the front; past items open read-only.
 */
export function Workspace() {
  const call = useNova(myCall);
  const ringing = useNova((s) => s.ringing);
  const testCall = useNova((s) => s.testCall);
  const wrap = useNova((s) => s.wrap);
  const selectedId = useNova((s) => s.selectedId);
  const user = useNova((s) => s.user);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [listOpen, setListOpen] = useState(true);
  const [scope, setScope] = useState<'mine' | 'team'>('mine');
  const [recent, setRecent] = useState<InteractionRow[] | null>(null);
  const select = (id: string | null) => useNova.getState().set({ selectedId: id });

  // Live work always takes the stage
  useEffect(() => {
    if (ringing) select(ringing.interactionId ?? 'ringing');
  }, [ringing?.interactionId, ringing?.from]);
  useEffect(() => {
    if (call) select(call.interactionId);
  }, [call?.interactionId]);
  useEffect(() => {
    if (wrap && !call) select(wrap.interactionId);
  }, [wrap?.interactionId]);

  // Reload the list when one of my interactions finishes
  const trigger = useNova(
    (s) => s.feed.find((e) => ['call.ended', 'call.abandoned', 'call.transferred', 'interaction.dispositioned'].includes(e.type))?.eventId,
  );
  useEffect(() => {
    let live = true;
    const qs = new URLSearchParams({ limit: '40' });
    if (scope === 'mine' && user) qs.set('agentId', user.sub);
    api<InteractionRow[]>('GET', `/interactions?${qs}`)
      .then((r) => live && setRecent(r))
      .catch(() => live && setRecent([]));
    return () => {
      live = false;
    };
  }, [trigger, scope, user]);

  const past = recent?.find((r) => r.id === selectedId) ?? null;
  const isLive = Boolean(call && call.interactionId === selectedId);
  const isRinging = Boolean(ringing) && (selectedId === 'ringing' || selectedId === ringing?.interactionId) && !isLive;
  const isWrap = Boolean(wrap && wrap.interactionId === selectedId && !isLive);
  const subject: Subject | null = isLive || isRinging
    ? fromCall(call, ringing?.from)
    : isWrap
      ? fromCall(wrap!.call)
      : past
        ? {
            interactionId: past.id,
            number: past.customer_number ?? '',
            name: rowName(past, user?.sub),
            line: lineOf(past.direction, past.queue_name),
          }
        : null;
  const noteKey = subject?.interactionId ?? null;

  return (
    <div className="flex h-full min-h-0">
      <WorkList
        open={listOpen}
        onToggle={() => setListOpen((v) => !v)}
        rows={recent}
        scope={scope}
        onScope={setScope}
        onSelect={select}
      />
      <div className="grid min-w-0 flex-1 grid-cols-1 overflow-auto xl:grid-cols-[minmax(0,1fr)_360px] xl:overflow-hidden">
        <section className="flex min-h-[560px] min-w-0 flex-col border-line xl:min-h-0 xl:border-r" aria-label="Interaction">
          {testCall && !isLive && !isRinging ? (
            <TestCallView />
          ) : isRinging || (isLive && call!.status !== 'connected') ? (
            <RingingView call={call} from={ringing?.from} answerable={Boolean(ringing)} />
          ) : isLive ? (
            <LiveView call={call!} />
          ) : isWrap ? (
            <WrapView notes={notes[wrap!.interactionId] ?? ''} onNotes={(v) => setNotes((n) => ({ ...n, [wrap!.interactionId]: v }))} />
          ) : past ? (
            <PastView row={past} />
          ) : (
            <IdleView />
          )}
        </section>
        <ContextPanel
          subject={subject}
          notes={noteKey ? notes[noteKey] ?? past?.notes ?? '' : ''}
          onNotes={noteKey && !past ? (v) => setNotes((n) => ({ ...n, [noteKey]: v })) : undefined}
        />
      </div>
    </div>
  );
}

function fromCall(call: LiveCall | null, ringingFrom?: string): Subject | null {
  if (!call) return ringingFrom ? { interactionId: 'ringing', number: ringingFrom, name: null, line: 'Inbound' } : null;
  if (call.direction === 'internal') {
    // The colleague on the other end, whichever side of the call I am
    const other = otherParty(call, useNova.getState());
    return { interactionId: call.interactionId, number: other.extension, name: other.name, line: other.team ? `Internal · ${other.team}` : 'Internal' };
  }
  return {
    interactionId: call.interactionId,
    number: call.customerNumber,
    name: call.customerName,
    line: lineOf(call.direction, call.queueName),
  };
}

/** Where a call came from, as lists show it: its queue, Outbound or Internal. */
function lineOf(direction: LiveCall['direction'], queueName: string | null): string {
  return direction === 'outbound' ? 'Outbound' : direction === 'internal' ? 'Internal' : queueName ?? 'Inbound';
}

function kindOf(direction: LiveCall['direction']): string {
  return direction === 'outbound' ? 'Outbound voice call' : direction === 'internal' ? 'Call with a colleague' : 'Voice call';
}

/** Who a past call was with: the customer, or on an internal call the other colleague. */
function rowName(r: InteractionRow, me: string | undefined): string | null {
  if (r.direction !== 'internal') return r.customer_name;
  return r.agent_id === me ? r.peer_name : r.agent_name;
}

// ---------------------------------------------------------------- work list
interface WorkItem {
  id: string;
  title: string;
  tag: string;
  at: number;
  tone: AgentState | 'past' | 'missed';
  status: string;
  needsAction: boolean;
}

const PAST_STATUS: Record<string, string> = {
  completed: 'Completed',
  wrap_up: 'Needs wrap-up',
  abandoned: 'Abandoned',
  failed: 'Not connected',
};

function WorkList({
  open,
  onToggle,
  rows,
  scope,
  onScope,
  onSelect,
}: {
  open: boolean;
  onToggle: () => void;
  rows: InteractionRow[] | null;
  scope: 'mine' | 'team';
  onScope: (s: 'mine' | 'team') => void;
  onSelect: (id: string) => void;
}) {
  const call = useNova(myCall);
  const ringing = useNova((s) => s.ringing);
  const wrap = useNova((s) => s.wrap);
  const selectedId = useNova((s) => s.selectedId);
  const role = useNova((s) => s.user?.role);
  const queues = useNova((s) => s.queues);
  const handled = useNova((s) => s.myStats.handled);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [oldestFirst, setOldestFirst] = useState(false);

  const items = useMemo<WorkItem[]>(() => {
    const list: WorkItem[] = [];
    if (call) {
      list.push({
        id: call.interactionId,
        title: call.direction === 'internal' ? otherParty(call, useNova.getState()).name : call.customerName ?? formatNumber(call.customerNumber),
        tag: lineOf(call.direction, call.queueName),
        at: call.answeredAt ?? call.startedAt,
        tone: call.status === 'connected' ? 'on_call' : 'reserved',
        status: call.status === 'connected' ? (call.onHold ? 'On hold' : 'Live') : 'Ringing',
        needsAction: false,
      });
    } else if (ringing) {
      list.push({ id: ringing.interactionId ?? 'ringing', title: formatNumber(ringing.from), tag: 'Inbound', at: Date.now(), tone: 'reserved', status: 'Ringing', needsAction: true });
    }
    if (wrap && wrap.interactionId !== call?.interactionId) {
      list.push({
        id: wrap.interactionId,
        title: wrap.customer,
        tag: lineOf(wrap.call.direction, wrap.call.queueName),
        at: wrap.call.startedAt,
        tone: 'wrap_up',
        status: 'Wrap-up',
        needsAction: true,
      });
    }
    const taken = new Set(list.map((i) => i.id));
    for (const r of rows ?? []) {
      if (taken.has(r.id)) continue;
      list.push({
        id: r.id,
        title: rowName(r, useNova.getState().user?.sub) ?? formatNumber(r.customer_number ?? 'Unknown'),
        tag: lineOf(r.direction, r.queue_name),
        at: Date.parse(r.started_at),
        tone: r.status === 'abandoned' || r.status === 'failed' ? 'missed' : 'past',
        status: r.disposition_label ?? PAST_STATUS[r.status] ?? r.status,
        needsAction: false,
      });
    }
    return list;
  }, [call, ringing, wrap, rows]);

  const live = items.filter((i) => i.tone !== 'past' && i.tone !== 'missed');
  const needle = query.trim().toLowerCase();
  const history = items
    .filter((i) => i.tone === 'past' || i.tone === 'missed')
    .filter((i) => !needle || i.title.toLowerCase().includes(needle) || i.title.replace(/\D/g, '').includes(needle.replace(/\D/g, '') || '\u0000'))
    .sort((a, b) => (oldestFirst ? a.at - b.at : b.at - a.at));
  const shown = [...live, ...history];

  if (!open) {
    return (
      <aside className="hidden w-11 shrink-0 flex-col items-center border-r border-line bg-hull/40 py-3 md:flex">
        <button type="button" onClick={onToggle} className="grid h-8 w-8 place-items-center rounded-lg text-ink-2 hover:bg-veil/[0.06] hover:text-ink" aria-label="Show work items">
          <Icon name="chevronRight" size={16} />
        </button>
        {live.length ? <span className="tabular mt-3 grid h-6 w-6 place-items-center rounded-full bg-break text-xs font-semibold text-white">{live.length}</span> : null}
      </aside>
    );
  }

  return (
    <aside className="hidden w-[300px] shrink-0 flex-col border-r border-line bg-hull/40 md:flex" aria-label="Work items">
      <div className="flex h-12 items-center gap-2 border-b border-line bg-gradient-to-r from-ion/15 to-transparent pl-4 pr-2">
        <Icon name="inbox" size={17} />
        <h2 className="text-sm font-semibold">Work</h2>
        <button type="button" onClick={onToggle} className="ml-auto grid h-7 w-7 place-items-center rounded-full border border-line bg-hull text-ink-2 hover:text-ink" aria-label="Hide work items">
          <Icon name="chevronLeft" size={14} />
        </button>
      </div>

      <div className="flex items-center gap-1 border-b border-line px-2 py-2">
        {role && role !== 'agent' ? (
          <select
            value={scope}
            onChange={(e) => onScope(e.target.value as 'mine' | 'team')}
            className="min-w-0 flex-1 rounded-lg border border-line bg-abyss/50 px-2 py-1.5 text-sm outline-none focus:border-ion"
            aria-label="Which work items"
          >
            <option value="mine">My work items</option>
            <option value="team">Whole team</option>
          </select>
        ) : (
          <span className="flex-1 px-2 text-sm text-ink-2">My work items</span>
        )}
        <ToolButton label="Search work items" active={searching} onClick={() => setSearching((v) => !v)}>
          <Icon name="search" size={15} />
        </ToolButton>
        <ToolButton label={oldestFirst ? 'Showing oldest first' : 'Showing newest first'} active={oldestFirst} onClick={() => setOldestFirst((v) => !v)}>
          <Icon name="history" size={15} />
        </ToolButton>
      </div>
      {searching ? (
        <div className="border-b border-line p-2">
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Name or number"
            className="w-full rounded-lg border border-line bg-abyss/50 px-3 py-1.5 text-sm outline-none focus:border-ion"
          />
        </div>
      ) : null}

      <ol className="flex-1 overflow-auto">
        {rows === null && !live.length ? <li className="px-4 py-6 text-sm text-ink-3">Loading work items…</li> : null}
        {rows !== null && shown.length === 0 ? (
          <li className="px-4 py-6 text-sm text-ink-2">{needle ? `Nothing matches "${query}".` : 'No work yet today. Calls you take appear here.'}</li>
        ) : null}
        {shown.map((it) => (
          <WorkRow key={it.id} item={it} selected={it.id === selectedId} onClick={() => onSelect(it.id)} />
        ))}
      </ol>

      <div className="border-t border-line px-4 py-3">
        <ul className="mb-2 flex flex-col gap-1">
          {queues.map((q) => (
            <li key={q.queue_id} className="flex items-center justify-between text-xs">
              <span className="text-ink-2">{q.name}</span>
              <span className={`tabular ${q.waiting ? 'text-wrapup' : 'text-ink-3'}`}>{q.waiting ? `${q.waiting} waiting` : 'clear'}</span>
            </li>
          ))}
        </ul>
        <p className="text-xs text-ink-3">
          {shown.length} {shown.length === 1 ? 'item' : 'items'}, {handled} handled today
        </p>
      </div>
    </aside>
  );
}

function ToolButton({ label, active, onClick, children }: { label: string; active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      className={`grid h-8 w-8 place-items-center rounded-lg transition ${active ? 'bg-ion/15 text-ion' : 'text-ink-2 hover:bg-veil/[0.06] hover:text-ink'}`}
    >
      {children}
    </button>
  );
}

function WorkRow({ item, selected, onClick }: { item: WorkItem; selected: boolean; onClick: () => void }) {
  const color =
    item.tone === 'past' ? 'var(--color-hull-2)' : item.tone === 'missed' ? 'color-mix(in srgb, var(--color-break) 18%, transparent)' : STATE_COLOR[item.tone as AgentState];
  const ink = item.tone === 'past' ? 'var(--color-ink-2)' : item.tone === 'missed' ? 'var(--color-break)' : 'var(--color-on-accent)';
  const today = new Date(item.at).toDateString() === new Date().toDateString();
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        aria-current={selected ? 'true' : undefined}
        className={`relative flex w-full items-start gap-3 border-b border-line/60 px-3 py-2.5 text-left transition ${
          selected ? 'bg-veil/[0.07]' : 'hover:bg-veil/[0.03]'
        }`}
      >
        {selected ? <span className="absolute inset-y-0 left-0 w-0.5 bg-ion" aria-hidden /> : null}
        <span className="relative grid h-8 w-8 shrink-0 place-items-center rounded-lg" style={{ background: color, color: ink }}>
          <Icon name="phone" size={15} />
          {item.needsAction ? <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-break ring-2 ring-hull" aria-label="Needs action" /> : null}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2">
            <span className="truncate text-sm font-medium">{item.title}</span>
            <time className="tabular shrink-0 text-xs text-ink-3">
              {new Date(item.at).toLocaleString([], today ? { hour: '2-digit', minute: '2-digit' } : { day: 'numeric', month: 'short' })}
            </time>
          </span>
          <span className="mt-1 flex items-center gap-2 text-xs">
            <span className="rounded border border-line px-1.5 py-px text-ink-2">{item.tag}</span>
            <span className={item.tone === 'missed' ? 'text-break' : item.tone === 'past' ? 'text-ink-3' : 'text-ink'}>{item.status}</span>
          </span>
        </span>
      </button>
    </li>
  );
}

// ---------------------------------------------------------------- interaction pane
function PaneHeader({ subject, kind, tone, right }: { subject: Subject | null; kind: string; tone: string; right?: React.ReactNode }) {
  const title = subject ? subject.name ?? formatNumber(subject.number) : '';
  return (
    <div className="flex h-14 shrink-0 items-center gap-3 border-b border-line px-4">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-on-accent" style={{ background: tone }}>
        <Icon name="phone" size={17} />
      </span>
      <div className="min-w-0">
        <p className="truncate font-semibold leading-tight">{title}</p>
        <p className="truncate text-xs text-ink-3">
          {kind}
          {subject?.interactionId && subject.interactionId !== 'ringing' ? ` #${subject.interactionId.slice(0, 8)}` : ''}
        </p>
      </div>
      <div className="ml-auto flex items-center gap-3">{right}</div>
    </div>
  );
}

function Banner({ color, children, right, muted = false }: { color: string; children: React.ReactNode; right?: React.ReactNode; muted?: boolean }) {
  return (
    <div
      className="flex shrink-0 items-center gap-3 px-4 py-2 text-sm font-medium"
      style={muted ? { background: `color-mix(in srgb, ${color} 22%, transparent)`, color: 'var(--color-ink)' } : { background: color, color: 'var(--color-on-accent)' }}
      role="status"
    >
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {right}
    </div>
  );
}

function RingingView({ call, from, answerable }: { call: LiveCall | null; from?: string; answerable: boolean }) {
  const subject = fromCall(call, from);
  const outbound = call?.direction === 'outbound';
  const internal = call?.direction === 'internal';
  const calledMe = useNova((s) => Boolean(call?.peerId && call.peerId === s.user?.sub));
  const answering = useNova((s) => Boolean(s.ringing?.answering));
  // A call I placed that has not been answered yet: I can give up on it
  const placedByMe = useNova((s) => Boolean(call && !answerable && call.direction !== 'inbound' && call.agentId === s.user?.sub));
  useEffect(() => {
    if (!placedByMe || !call) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey && e.key.toLowerCase() === 'x') {
        e.preventDefault();
        void hangup(call);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [placedByMe, call?.interactionId]);
  useEffect(() => {
    if (!answerable) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        softphone.answer();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [answerable]);
  return (
    <>
      <PaneHeader subject={subject} kind={kindOf(call?.direction ?? 'inbound')} tone={STATE_COLOR.reserved} />
      <Banner
        color={STATE_COLOR.reserved}
        right={call?.queuedAt && !outbound ? (<span className="tabular">Waited <Elapsed since={call.queuedAt} /></span>) : null}
      >
        {internal
          ? calledMe
            ? `${subject?.name ?? 'A colleague'} is calling you`
            : `Calling ${subject?.name ?? 'your colleague'}`
          : outbound
            ? 'Calling the customer'
            : `Incoming call${call?.queueName ? ` on the ${call.queueName} line` : ''}`}
      </Banner>
      <div className="flex flex-1 flex-col items-center justify-center gap-6 p-6">
        <SignalRing color={STATE_COLOR.reserved} mode="ringing" size={280}>
          <span className="text-reserved">
            <Icon name="phone" size={30} />
          </span>
          <p className="mt-2 max-w-[180px] truncate text-lg font-semibold">{subject?.name ?? formatNumber(subject?.number ?? '')}</p>
        </SignalRing>
        {answerable ? (
          <div className="flex items-center gap-3">
            <Button tone="success" className="min-w-36 py-3 text-base" onClick={() => softphone.answer()} disabled={answering} autoFocus>
              <Icon name="phone" /> {answering ? 'Connecting…' : 'Answer'}
            </Button>
            <Button tone="quiet" className="py-3" onClick={() => softphone.decline()} disabled={answering}>
              Decline
            </Button>
          </div>
        ) : (
          <p className="text-sm text-ink-2">
            {internal ? `Ringing ${subject?.name ?? 'them'}…` : outbound ? 'Your softphone answered; dialing the customer…' : 'Ringing your softphone…'}
          </p>
        )}
        {placedByMe ? (
          <Button tone="danger" className="min-w-36 py-3 text-base" onClick={() => void hangup(call!)} title="Alt+X">
            <Icon name="hangup" /> Hang up
          </Button>
        ) : null}
        {answerable ? (
          <p className="text-xs text-ink-3">
            <Kbd>Alt</Kbd> + <Kbd>A</Kbd> to answer
          </p>
        ) : placedByMe ? (
          <p className="text-xs text-ink-3">
            <Kbd>Alt</Kbd> + <Kbd>X</Kbd> to hang up
          </p>
        ) : null}
      </div>
    </>
  );
}

function LiveView({ call }: { call: LiveCall }) {
  const me = useNova((s) => s.user?.sub);
  if (call.third?.agentId === me && call.agentId !== me) return <ConsultedView call={call} />;
  if (call.peerId === me) return <CalledByColleagueView call={call} />;
  return <OwnLiveView call={call} />;
}

/**
 * A colleague called me. The call is theirs in the API (hold is theirs to
 * use), so my controls work on this device: mute my microphone, hang up.
 */
function CalledByColleagueView({ call }: { call: LiveCall }) {
  const muted = useNova((s) => s.phoneMuted);
  const subject = fromCall(call);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === 'm') softphone.setMuted(!useNova.getState().phoneMuted);
      else if (k === 'x') softphone.hangupLocal();
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <>
      <PaneHeader subject={subject} kind="Call with a colleague" tone={STATE_COLOR.on_call} />
      <Banner color={STATE_COLOR.on_call} muted={call.onHold} right={<Elapsed since={call.answeredAt} className="text-sm" />}>
        {call.onHold
          ? `${subject?.name ?? 'Your colleague'} put you on hold`
          : call.third?.status === 'conference'
            ? `In a conference with ${subject?.name ?? 'your colleague'} and ${call.third.name}`
            : `Talking to ${subject?.name ?? 'a colleague'}`}
      </Banner>
      <div className="flex shrink-0 flex-wrap items-center gap-1 border-b border-line px-3 py-2" role="toolbar" aria-label="Call controls">
        <Button tone="quiet" className="px-3 py-2" onClick={() => softphone.setMuted(!muted)} aria-pressed={muted} title="Alt+M">
          <Icon name={muted ? 'micOff' : 'mic'} size={17} /> {muted ? 'Unmute' : 'Mute'}
        </Button>
        <Button tone="danger" className="ml-auto px-3.5 py-2" onClick={() => softphone.hangupLocal()} title="Alt+X">
          <Icon name="hangup" size={17} /> Hang up
        </Button>
      </div>
      <div className="flex flex-col items-center gap-4 p-6">
        <SignalRing color={STATE_COLOR.on_call} mode={call.onHold ? 'hold' : 'live'} size={300}>
          <Elapsed since={call.answeredAt} className="text-4xl text-ink" />
          <p className={`mt-2 text-sm ${call.onHold ? 'text-wrapup' : 'text-ink-3'}`}>{call.onHold ? 'On hold' : muted ? 'You are muted' : 'Talking'}</p>
        </SignalRing>
        <div className="text-center">
          <p className="text-xl font-semibold tracking-tight">{subject?.name}</p>
          <p className="tabular mt-1 text-sm text-ink-2">{[subject?.number ? `Extension ${subject.number}` : null, subject?.line].filter(Boolean).join(' · ')}</p>
        </div>
      </div>
    </>
  );
}

/** I was brought into someone else's call to help: talk, then leave or take it over when they hand it to me. */
function ConsultedView({ call }: { call: LiveCall }) {
  const subject = fromCall(call);
  const conference = call.third?.status === 'conference';
  return (
    <>
      <PaneHeader subject={subject} kind="Consultation" tone={STATE_COLOR.on_call} />
      <Banner color={STATE_COLOR.on_call} right={null}>
        {conference
          ? `In a conference with ${call.agentName ?? 'an agent'} and ${call.direction === 'internal' ? subject?.name : 'the customer'}`
          : `${call.agentName ?? 'An agent'} is consulting you; ${call.direction === 'internal' ? subject?.name : 'the customer'} is on hold`}
      </Banner>
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-line px-3 py-2" role="toolbar" aria-label="Consultation controls">
        <Button tone="quiet" className="px-3 py-2" onClick={() => void toggleMute(call)}>
          <Icon name={call.muted ? 'micOff' : 'mic'} size={17} /> {call.muted ? 'Unmute' : 'Mute'}
        </Button>
        <Button tone="danger" className="ml-auto px-3.5 py-2" onClick={() => void act(() => api('POST', `/calls/${call.interactionId}/consult/cancel`), 'You left the call')}>
          <Icon name="hangup" size={17} /> Leave
        </Button>
      </div>
      <div className="flex flex-col items-center gap-3 p-8 text-center">
        <p className="text-xl font-semibold tracking-tight">{call.customerName ?? formatNumber(call.customerNumber)}</p>
        <p className="max-w-[360px] text-sm text-ink-2">
          If {call.agentName ?? 'they'} hand{call.agentName ? 's' : ''} the call to you, it becomes yours here, with the usual controls.
        </p>
      </div>
    </>
  );
}

/** While consulting or in a conference: what the agent can do next. */
function ThirdPartyBar({ call }: { call: LiveCall }) {
  const raw = call.third!;
  const third = { ...raw, name: raw.kind === 'number' ? formatNumber(raw.name) : raw.name };
  const go = (path: string, ok: string) => void act(() => api('POST', `/calls/${call.interactionId}/consult/${path}`), ok);
  const held = call.direction === 'internal' ? otherParty(call, useNova.getState()).name : 'the customer';
  const text =
    third.status === 'ringing'
      ? `Calling ${third.name}…`
      : third.status === 'consulting'
        ? `Talking to ${third.name}. ${held[0].toUpperCase()}${held.slice(1)} is on hold.`
        : `Conference: you, ${held} and ${third.name}`;
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-line bg-ion/[0.06] px-4 py-2.5" role="region" aria-label="Consultation">
      <span className="text-sm">{text}</span>
      <span className="ml-auto flex flex-wrap gap-1.5">
        {third.status === 'consulting' ? (
          <>
            <Button tone="primary" className="px-3 py-1.5 text-sm" onClick={() => go('complete', `Transferred to ${third.name}`)}>
              Transfer to {third.kind === 'agent' ? third.name.split(' ')[0] : 'them'}
            </Button>
            <Button tone="quiet" className="px-3 py-1.5 text-sm" onClick={() => go('conference', 'Conference started')}>
              Conference
            </Button>
          </>
        ) : null}
        {third.status === 'conference' ? (
          <Button tone="quiet" className="px-3 py-1.5 text-sm" onClick={() => go('complete', `You left; ${third.name} has the call`)}>
            Hand over and leave
          </Button>
        ) : null}
        <Button tone="ghost" className="px-3 py-1.5 text-sm" onClick={() => go('cancel', third.status === 'conference' ? `${third.name} left the call` : 'Consultation cancelled')}>
          {third.status === 'conference' ? `Drop ${third.kind === 'agent' ? third.name.split(' ')[0] : 'them'}` : 'Cancel'}
        </Button>
      </span>
    </div>
  );
}

function OwnLiveView({ call }: { call: LiveCall }) {
  const [panel, setPanel] = useState<'none' | 'keypad' | 'transfer'>('none');

  // Alt+H hold, Alt+M mute, Alt+T transfer, Alt+K keypad, Alt+X hang up
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === 'h') void toggleHold(call);
      else if (k === 'm') void toggleMute(call);
      else if (k === 't') setPanel((p) => (p === 'transfer' ? 'none' : 'transfer'));
      else if (k === 'k' && call.direction !== 'internal') setPanel((p) => (p === 'keypad' ? 'none' : 'keypad'));
      else if (k === 'r' && call.recording) void toggleRecordingPause(call);
      else if (k === 'x') void hangup(call);
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [call]);

  const subject = fromCall(call);
  return (
    <>
      <PaneHeader
        subject={subject}
        kind={kindOf(call.direction)}
        tone={STATE_COLOR.on_call}
        right={
          call.recording ? (
            <span className={`inline-flex items-center gap-1.5 text-xs ${call.recordingPaused ? 'text-wrapup' : 'text-break'}`}>
              <Icon name={call.recordingPaused ? 'pause' : 'rec'} size={9} /> {call.recordingPaused ? 'Recording paused' : 'Recording'}
            </span>
          ) : null
        }
      />
      <Banner
        color={STATE_COLOR.on_call}
        muted={call.onHold}
        right={<Elapsed since={call.answeredAt} className="text-sm" />}
      >
        {call.onHold
          ? `On hold. ${call.direction === 'internal' ? 'They hear' : 'The caller hears'} music.`
          : call.direction === 'internal'
            ? `Talking to ${subject?.name ?? 'your colleague'}`
            : `Connected${call.queueName ? ` on the ${call.queueName} line` : ''}`}
      </Banner>

      <CallToolbar call={call} panel={panel} setPanel={setPanel} />
      {call.third ? <ThirdPartyBar call={call} /> : null}

      <div className="flex-1 overflow-auto">
        <div className="flex flex-col items-center gap-4 p-6">
          <SignalRing color={STATE_COLOR.on_call} mode={call.onHold ? 'hold' : 'live'} size={300}>
            <Elapsed since={call.answeredAt} className="text-4xl text-ink" />
            <p className={`mt-2 text-sm ${call.onHold ? 'text-wrapup' : 'text-ink-3'}`}>{call.onHold ? 'On hold' : call.muted ? 'You are muted' : 'Talking'}</p>
          </SignalRing>
          <div className="text-center">
            <p className="text-xl font-semibold tracking-tight">{subject?.name ?? formatNumber(call.customerNumber)}</p>
            {call.direction === 'internal' ? (
              <p className="tabular mt-1 text-sm text-ink-2">{[`Extension ${subject?.number}`, subject?.line].filter(Boolean).join(' · ')}</p>
            ) : call.customerName ? (
              <p className="tabular mt-1 text-sm text-ink-2">{formatNumber(call.customerNumber)}</p>
            ) : null}
          </div>
        </div>
        {panel === 'keypad' ? <Keypad path={`/calls/${call.interactionId}/dtmf`} /> : null}
        {panel === 'transfer' ? <TransferPicker call={call} onDone={() => setPanel('none')} /> : null}
      </div>
    </>
  );
}

/** Call controls, in one row under the banner. */
function CallToolbar({ call, panel, setPanel }: { call: LiveCall; panel: 'none' | 'keypad' | 'transfer'; setPanel: (p: 'none' | 'keypad' | 'transfer') => void }) {
  const tool = 'flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition';
  const idle = 'text-ink-2 hover:bg-veil/[0.06] hover:text-ink';
  const on = 'bg-ion/15 text-ion';
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-1 border-b border-line px-3 py-2" role="toolbar" aria-label="Call controls">
      <button type="button" className={`${tool} ${call.muted ? on : idle}`} onClick={() => void toggleMute(call)} aria-pressed={call.muted} title="Alt+M">
        <Icon name={call.muted ? 'micOff' : 'mic'} size={17} />
        {call.muted ? 'Unmute' : 'Mute'}
      </button>
      <button type="button" className={`${tool} ${call.onHold ? on : idle}`} onClick={() => void toggleHold(call)} aria-pressed={call.onHold} title="Alt+H">
        <Icon name={call.onHold ? 'play' : 'pause'} size={17} />
        {call.onHold ? 'Resume' : 'Hold'}
      </button>
      {/* A call between colleagues has no menus to key into */}
      {call.direction !== 'internal' ? (
        <button type="button" className={`${tool} ${panel === 'keypad' ? on : idle}`} onClick={() => setPanel(panel === 'keypad' ? 'none' : 'keypad')} title="Alt+K">
          <Icon name="keypad" size={17} />
          Keypad
        </button>
      ) : null}
      <button type="button" className={`${tool} ${panel === 'transfer' ? on : idle}`} onClick={() => setPanel(panel === 'transfer' ? 'none' : 'transfer')} title="Alt+T">
        <Icon name="transfer" size={17} />
        {call.direction === 'internal' ? 'Transfer or conference' : 'Transfer'}
      </button>
      {call.recording ? (
        <button
          type="button"
          className={`${tool} ${call.recordingPaused ? 'bg-wrapup/15 text-wrapup' : idle}`}
          onClick={() => void toggleRecordingPause(call)}
          aria-pressed={call.recordingPaused}
          title="Alt+R. Pause while the caller gives card details; the paused part is not recorded"
        >
          <Icon name={call.recordingPaused ? 'rec' : 'pause'} size={17} />
          {call.recordingPaused ? 'Resume recording' : 'Pause recording'}
        </button>
      ) : null}
      <span className="ml-auto hidden text-xs text-ink-3 2xl:inline">
        <Kbd>Alt</Kbd> + <Kbd>M</Kbd> <Kbd>H</Kbd> <Kbd>K</Kbd> <Kbd>T</Kbd> <Kbd>X</Kbd>
      </span>
      <Button tone="danger" className="ml-auto px-3.5 py-2 2xl:ml-3" onClick={() => void hangup(call)} title="Alt+X">
        <Icon name="hangup" size={17} /> Hang up
      </Button>
    </div>
  );
}

function Keypad({ path }: { path: string }) {
  const [sent, setSent] = useState('');
  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];
  return (
    <div className="mx-auto max-w-[260px] border-t border-line px-6 py-5">
      <p className="tabular mb-3 h-6 text-center text-lg text-ink-2" aria-live="polite">
        {sent || ' '}
      </p>
      <div className="grid grid-cols-3 gap-2">
        {keys.map((k) => (
          <button
            key={k}
            type="button"
            className="tabular rounded-xl border border-line py-3 text-lg hover:border-ion hover:text-ion"
            onClick={() => {
              setSent((s) => (s + k).slice(-16));
              void act(() => api('POST', path, { digits: k }));
            }}
          >
            {k}
          </button>
        ))}
      </div>
    </div>
  );
}

type Target = { type: 'queue'; queueId: string } | { type: 'agent'; agentId: string } | { type: 'number'; number: string };

function TransferPicker({ call, onDone }: { call: LiveCall; onDone: () => void }) {
  const queues = useNova((s) => s.queueList);
  const people = useNova((s) => s.people);
  const agents = useNova((s) => s.agents);
  const user = useNova((s) => s.user);
  const [number, setNumber] = useState('');
  const available = Object.values(people).filter((p) => p.id !== user?.sub && agents[p.id]?.state === 'available');
  const digits = number.replace(/[^\d+]/g, '');

  async function send(target: Target, label: string) {
    const ok = await act(() => api('POST', `/calls/${call.interactionId}/transfer`, target), `Transferred to ${label}`);
    if (ok) onDone();
  }
  /** Talk to them first: the customer waits on hold. */
  async function consult(target: Exclude<Target, { type: 'queue' }>, label: string) {
    const held = call.direction === 'internal' ? otherParty(call, useNova.getState()).name : 'the customer';
    const ok = await act(() => api('POST', `/calls/${call.interactionId}/consult`, target), `Calling ${label}; ${held} is on hold`);
    if (ok) onDone();
  }
  if (call.third) return <p className="border-t border-line p-6 text-sm text-ink-2">Finish the current consultation first.</p>;

  if (call.direction === 'internal') {
    // Between colleagues: call someone else in, then hand the call over or talk all together
    const them = otherParty(call, useNova.getState()).name;
    const others = available.filter((p) => p.id !== call.peerId);
    return (
      <div className="border-t border-line p-6">
        <p className="mb-1 text-sm text-ink-3">Bring in a colleague</p>
        <p className="mb-3 text-xs text-ink-3">{them} waits on hold while you talk to them; then transfer the call or start a conference.</p>
        {others.length === 0 ? (
          <p className="text-sm text-ink-2">Nobody else is available right now.</p>
        ) : (
          <ul className="flex max-w-[480px] flex-col gap-1.5">
            {others.map((p) => (
              <li key={p.id} className="flex items-center gap-1.5">
                <span className="flex min-w-0 flex-1 items-center gap-2 text-sm">
                  <StateDot state="available" size={8} /> <span className="truncate">{p.name}</span>
                  <span className="tabular text-ink-3">{p.extension}</span>
                </span>
                <Button tone="quiet" className="px-2.5 py-1.5 text-xs" onClick={() => void consult({ type: 'agent', agentId: p.id }, p.name)} aria-label={`Call ${p.name} in`}>
                  Call in
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className="grid gap-5 border-t border-line p-6 md:grid-cols-2">
      <div>
        <p className="mb-2 text-sm text-ink-3">To a queue</p>
        <ul className="flex flex-col gap-1.5">
          {queues.map((q) => (
            <li key={q.id}>
              <Button className="w-full justify-between" onClick={() => void send({ type: 'queue', queueId: q.id }, q.name)}>
                {q.name}
                <Icon name="transfer" size={16} />
              </Button>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <p className="mb-2 text-sm text-ink-3">To an available agent</p>
        {available.length === 0 ? (
          <p className="text-sm text-ink-2">Nobody else is available right now.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {available.map((p) => (
              <li key={p.id} className="flex items-center gap-1.5">
                <span className="flex min-w-0 flex-1 items-center gap-2 text-sm">
                  <StateDot state="available" size={8} /> <span className="truncate">{p.name}</span>
                  <span className="tabular text-ink-3">{p.extension}</span>
                </span>
                <Button tone="quiet" className="px-2.5 py-1.5 text-xs" title="The customer goes to them now" onClick={() => void send({ type: 'agent', agentId: p.id }, p.name)}>
                  Transfer
                </Button>
                <Button tone="quiet" className="px-2.5 py-1.5 text-xs" title="Talk to them first; the customer waits on hold" onClick={() => void consult({ type: 'agent', agentId: p.id }, p.name)}>
                  Consult
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="md:col-span-2">
        <p className="mb-2 text-sm text-ink-3">To a number</p>
        <div className="flex flex-wrap gap-1.5">
          <input
            aria-label="Number to transfer to"
            inputMode="tel"
            placeholder="0712 345 678 or +254…"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            className="tabular min-w-0 flex-1 rounded-lg border border-line bg-abyss/50 px-3 py-2 text-sm outline-none focus:border-ion"
          />
          <Button tone="quiet" className="px-3 py-2 text-sm" disabled={digits.length < 3} title="The customer goes to this number now" onClick={() => void send({ type: 'number', number: digits }, formatNumber(digits))}>
            Transfer
          </Button>
          <Button tone="quiet" className="px-3 py-2 text-sm" disabled={digits.length < 3} title="Talk to them first; the customer waits on hold" onClick={() => void consult({ type: 'number', number: digits }, formatNumber(digits))}>
            Consult
          </Button>
        </div>
        <p className="mt-1.5 text-xs text-ink-3">Consult to talk first, then transfer or bring everyone into a conference.</p>
      </div>
    </div>
  );
}

function WrapView({ notes, onNotes }: { notes: string; onNotes: (v: string) => void }) {
  const wrap = useNova((s) => s.wrap)!;
  const dispositions = useNova((s) => s.dispositions);
  const me = useNova((s) => s.me);
  const [chosen, setChosen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useNow();
  const subject = fromCall(wrap.call);

  async function save() {
    if (!chosen) return;
    setBusy(true);
    const ok = await act(() => api('POST', `/interactions/${wrap.interactionId}/disposition`, { dispositionId: chosen, notes }), 'Call wrapped up');
    setBusy(false);
    if (ok) useNova.getState().set({ wrap: null, selectedId: null });
  }

  return (
    <>
      <PaneHeader subject={subject} kind={kindOf(wrap.call.direction)} tone={STATE_COLOR.wrap_up} />
      <Banner color={STATE_COLOR.wrap_up} right={me?.state === 'wrap_up' ? <Elapsed since={me.since} className="text-sm" /> : null}>
        Wrapping up interaction
      </Banner>
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-[640px]">
          <h2 className="mb-1 text-lg font-medium">Wrap up the call with {wrap.customer}</h2>
          <p className="mb-6 text-sm text-ink-3">
            Talked {duration(wrap.talkSeconds)}
            {wrap.call.queuedAt && wrap.call.answeredAt ? `, waited ${duration((wrap.call.answeredAt - wrap.call.queuedAt) / 1000)}` : ''}
          </p>
          <p className="mb-2 text-sm font-medium">Outcome</p>
          <div className="mb-6 flex flex-wrap gap-2" role="radiogroup" aria-label="Outcome">
            {dispositions.map((d) => (
              <button
                key={d.id}
                type="button"
                role="radio"
                aria-checked={chosen === d.id}
                onClick={() => setChosen(d.id)}
                className={`rounded-lg border px-3.5 py-2 text-sm transition ${
                  chosen === d.id ? 'border-wrapup bg-wrapup/15 text-ink' : 'border-line text-ink-2 hover:border-line-strong hover:text-ink'
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
          <label className="block">
            <span className="mb-2 block text-sm font-medium">Notes</span>
            <textarea
              rows={6}
              value={notes}
              onChange={(e) => onNotes(e.target.value)}
              placeholder="What did the customer need, and what did you do?"
              className="w-full resize-y rounded-lg border border-line bg-abyss/60 px-3.5 py-2.5 text-sm outline-none focus:border-wrapup"
            />
          </label>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-3 border-t border-line px-6 py-3">
        <Button tone="primary" onClick={() => void save()} disabled={!chosen || busy}>
          {busy ? 'Saving…' : 'Save and finish'}
        </Button>
        <p className="text-sm text-ink-3">{chosen ? 'You go back to Available after saving.' : wrap.required ? 'Choose an outcome to finish.' : 'Optional for this queue.'}</p>
      </div>
    </>
  );
}

function PastView({ row }: { row: InteractionRow }) {
  const [audio, setAudio] = useState<string | null>(null);
  const missed = row.status === 'abandoned' || row.status === 'failed';
  const subject: Subject = {
    interactionId: row.id,
    number: row.customer_number ?? '',
    name: rowName(row, useNova.getState().user?.sub),
    line: lineOf(row.direction, row.queue_name),
  };
  useEffect(() => setAudio(null), [row.id]);

  async function play() {
    const r = await act(() => api<{ url: string }>('GET', `/interactions/${row.id}/recording-url`));
    if (r) setAudio(r.url);
  }

  const facts: [string, string][] = [
    ['Started', new Date(row.started_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })],
    ['Line', subject.line],
    ['Agent', row.agent_name ?? 'Nobody'],
    ['Waited', row.wait_seconds != null ? duration(row.wait_seconds) : 'No wait'],
    ['Talked', row.talk_seconds ? duration(row.talk_seconds) : 'Not connected'],
    ['On hold', duration(row.hold_seconds ?? 0)],
  ];

  return (
    <>
      <PaneHeader
        subject={subject}
        kind={kindOf(row.direction)}
        tone={missed ? 'var(--color-break)' : 'var(--color-ink-3)'}
        right={
          row.customer_number ? (
            <Button tone="quiet" className="px-3 py-1.5" onClick={() => void dial(row.customer_number!)}>
              <Icon name="phone" size={15} /> Call back
            </Button>
          ) : null
        }
      />
      <Banner color={missed ? 'var(--color-break)' : 'var(--color-ink-3)'} muted>
        {missed ? (row.status === 'abandoned' ? 'The caller hung up before anyone answered' : 'This call did not connect') : row.disposition_label ?? 'Completed'}
      </Banner>
      <div className="flex-1 overflow-auto p-6">
        <dl className="grid max-w-[640px] grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
          {facts.map(([k, v]) => (
            <div key={k}>
              <dt className="text-xs text-ink-3">{k}</dt>
              <dd className="mt-0.5 text-sm">{v}</dd>
            </div>
          ))}
        </dl>
        <div className="mt-6 max-w-[640px]">
          <p className="mb-2 text-sm font-medium">Notes</p>
          <p className="whitespace-pre-wrap text-sm text-ink-2">{row.notes || 'No notes.'}</p>
        </div>
        {row.has_recording ? (
          <div className="mt-6 max-w-[640px]">
            <p className="mb-2 text-sm font-medium">Recording</p>
            {audio ? (
              <audio src={audio} controls autoPlay className="h-10 w-full" />
            ) : (
              <Button onClick={() => void play()}>
                <Icon name="play" size={15} /> Play recording
              </Button>
            )}
          </div>
        ) : null}
      </div>
    </>
  );
}

/**
 * My side of a test call to one of our own numbers: I am the caller, so I
 * get the keypad for the IVR menus, mute and hang-up, and see where the call
 * has got to (menu, queue, agent).
 */
function TestCallView() {
  const test = useNova((s) => s.testCall)!;
  const live = useNova(myTestCall);
  const [keypad, setKeypad] = useState(true);
  const press = (k: string) => void act(() => api('POST', '/calls/test/dtmf', { digits: k }));

  // Digits typed on the keyboard go to the menu too; Alt+M mute, Alt+K keypad, Alt+X hang up
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase();
      if (e.altKey && k === 'm') softphone.setMuted(!useNova.getState().testCall?.muted);
      else if (e.altKey && k === 'k') setKeypad((v) => !v);
      else if (e.altKey && k === 'x') softphone.hangupLocal();
      else if (!e.altKey && !e.ctrlKey && !e.metaKey && /^[0-9*#]$/.test(e.key) && !(e.target as HTMLElement).closest('input, textarea')) press(e.key);
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const stage = !test.since
    ? 'Calling…'
    : !live
      ? 'In the menu. Use the keypad to choose options.'
      : live.status === 'queued'
        ? `Waiting in the ${live.queueName ?? ''} queue`
        : live.status === 'ringing'
          ? `Ringing ${live.agentName ?? 'an agent'}`
          : live.onHold
            ? `On hold by ${live.agentName ?? 'the agent'}`
            : `Talking to ${live.agentName ?? 'an agent'}`;
  const tool = 'flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition';
  const idle = 'text-ink-2 hover:bg-veil/[0.06] hover:text-ink';
  const on = 'bg-ion/15 text-ion';
  return (
    <>
      <PaneHeader subject={{ interactionId: live?.interactionId ?? 'ringing', number: test.number, name: null, line: 'Test call' }} kind="Test call to our own number" tone={STATE_COLOR.on_call} />
      <Banner color={STATE_COLOR.on_call} muted={!test.since || Boolean(live?.onHold)} right={test.since ? <Elapsed since={test.since} className="text-sm" /> : null}>
        {stage}
      </Banner>
      <div className="flex shrink-0 flex-wrap items-center gap-1 border-b border-line px-3 py-2" role="toolbar" aria-label="Call controls">
        <button type="button" className={`${tool} ${test.muted ? on : idle}`} onClick={() => softphone.setMuted(!test.muted)} aria-pressed={test.muted} title="Alt+M">
          <Icon name={test.muted ? 'micOff' : 'mic'} size={17} />
          {test.muted ? 'Unmute' : 'Mute'}
        </button>
        <button type="button" className={`${tool} ${keypad ? on : idle}`} onClick={() => setKeypad((v) => !v)} aria-pressed={keypad} title="Alt+K">
          <Icon name="keypad" size={17} />
          Keypad
        </button>
        <Button tone="danger" className="ml-auto px-3.5 py-2" onClick={() => softphone.hangupLocal()} title="Alt+X">
          <Icon name="hangup" size={17} /> Hang up
        </Button>
      </div>
      <div className="flex-1 overflow-auto">
        <div className="flex flex-col items-center gap-2 p-6 text-center">
          <p className="text-xl font-semibold tracking-tight">{formatNumber(test.number)}</p>
          <p className="max-w-sm text-sm text-ink-2">You are the caller. The call goes through the number's menu and queue as a customer's would; your status is Away until it ends.</p>
        </div>
        {keypad ? <Keypad path="/calls/test/dtmf" /> : null}
      </div>
    </>
  );
}

function IdleView() {
  const me = useNova((s) => s.me);
  const phone = useNova((s) => s.phone);
  const phoneError = useNova((s) => s.phoneError);
  const state = me?.state ?? 'offline';
  const copy: Record<AgentState, string> = {
    available: 'The next caller in your queues will ring here.',
    reserved: 'A call is ringing.',
    on_call: 'You are on a call.',
    wrap_up: 'Finish your wrap-up to take the next call.',
    break: 'Calls will not ring until you are available.',
    away: 'Calls will not ring until you are available.',
    offline: 'Go available to start taking calls.',
  };
  const noPhone = phone === 'failed' || phone === 'off';
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-5 p-6 text-center">
      <SignalRing color={STATE_COLOR[state]} mode="idle" size={300}>
        {noPhone ? (
          <p className="text-lg font-medium text-ink-2">No phone</p>
        ) : (
          <>
            <p className="text-2xl font-medium">{STATE_LABEL[state]}</p>
            {me ? <Elapsed since={me.since} className="mt-1 text-sm text-ink-3" /> : null}
          </>
        )}
      </SignalRing>
      {noPhone ? (
        <>
          <p className="max-w-[380px] text-sm text-ink-2">{phoneError ?? 'Your softphone is not connected, so calls cannot ring here.'}</p>
          <Button tone="quiet" onClick={() => void softphone.start()}>
            Reconnect softphone
          </Button>
        </>
      ) : phone === 'connecting' ? (
        <p className="text-sm text-ink-2">Connecting your softphone…</p>
      ) : (
        <>
          <p className="max-w-[380px] text-sm text-ink-2">{copy[state]}</p>
          {state === 'break' || state === 'away' || state === 'offline' ? (
            <Button tone="success" onClick={() => void setMyState('available')}>
              Go available
            </Button>
          ) : null}
        </>
      )}
      <p className="hidden text-xs text-ink-3 md:block">
        Pick a past item on the left to see its details, or press <Kbd>Ctrl</Kbd> + <Kbd>K</Kbd> to call a number.
      </p>
    </div>
  );
}
